import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hire_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 응답 시 토큰 삭제 + 관리자 로그인 페이지 이동
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('hire_admin_token');
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  },
);

export const adminAuthApi = {
  login: (email: string, password: string) =>
    api.post('/admin/auth/login', { email, password }).then((res) => res.data),
  getMe: () =>
    api.get('/admin/auth/me').then((res) => res.data),
};

export const adminApi = {
  getStats: () =>
    api.get('/admin/stats').then((res) => res.data),

  getApplicants: (params?: { status?: string; search?: string; page?: number; includeTest?: boolean; campaign_id?: string; include_deleted?: boolean }) =>
    api.get('/admin/applicants', { params }).then((res) => res.data),

  getApplicantDetail: (id: number) =>
    api.get(`/admin/applicants/${id}`).then((res) => res.data),

  updateStatus: (id: number, status: string, reason?: string) =>
    api.put(`/admin/applicants/${id}/status`, { status, reason }).then((res) => res.data),

  bulkUpdateStatus: (ids: number[], status: string) =>
    api.put('/admin/applicants/bulk-status', { ids, status }).then((res) => res.data),

  // AI 평가
  runAiEvaluation: (applicantId: number) =>
    api.post(`/admin/ai-evaluation/${applicantId}`).then((res) => res.data),

  getAiEvaluation: (applicantId: number) =>
    api.get(`/admin/ai-evaluation/${applicantId}`).then((res) => res.data),

  // 오퍼
  createOffer: (data: any) =>
    api.post('/admin/offers', data).then((res) => res.data),

  sendOffer: (offerId: number) =>
    api.post(`/admin/offers/${offerId}/send`).then((res) => res.data),

  previewOfferPdf: (data: any) =>
    api.post('/admin/offers/preview-pdf', data, { responseType: 'blob' }).then((res) => res.data),

  // 시험 결과 조회
  getTestResult: (applicantTestId: number) =>
    api.get(`/written-test/admin/results/${applicantTestId}`).then((res) => res.data),

  // 시험 평가
  evaluateTest: (applicantTestId: number, evaluation: string, evaluationScore: number) =>
    api.put(`/written-test/admin/results/${applicantTestId}/evaluate`, { evaluation, evaluation_score: evaluationScore }).then((res) => res.data),

  // 시험 리셋 (재응시 허용)
  resetApplicantTest: (applicantTestId: number) =>
    api.post(`/written-test/admin/results/${applicantTestId}/reset`).then((res) => res.data),

  // 채용 설정
  getRecruitmentStatus: () =>
    api.get('/auth/recruitment-status').then((res) => res.data),

  setRecruitmentOpen: (open: boolean) =>
    api.put('/admin/settings/recruitment', { open }).then((res) => res.data),

  // 테스트 계정 설정
  getTestAccountVisible: () =>
    api.get('/admin/settings/test-account').then((res) => res.data),

  setTestAccountVisible: (visible: boolean) =>
    api.put('/admin/settings/test-account', { visible }).then((res) => res.data),

  // 캠페인 관리
  getCampaigns: () =>
    api.get('/admin/campaigns').then((res) => res.data),

  createCampaign: (data: { name: string; description?: string; start_date: string; end_date: string }) =>
    api.post('/admin/campaigns', data).then((res) => res.data),

  updateCampaign: (id: number, data: { name?: string; description?: string; start_date?: string; end_date?: string; is_active?: boolean }) =>
    api.put(`/admin/campaigns/${id}`, data).then((res) => res.data),

  closeCampaign: (id: number) =>
    api.put(`/admin/campaigns/${id}/close`).then((res) => res.data),

  // 지원자-캠페인 배정
  assignCampaign: (applicantId: number, campaignId: number | null) =>
    api.put(`/admin/applicants/${applicantId}/campaign`, { campaign_id: campaignId }).then((res) => res.data),

  bulkAssignCampaign: (ids: number[], campaignId: number) =>
    api.put('/admin/applicants/bulk-campaign', { ids, campaign_id: campaignId }).then((res) => res.data),

  // 지원자 삭제
  softDeleteApplicant: (id: number, reason: string) =>
    api.delete(`/admin/applicants/${id}`, { data: { reason } }).then((res) => res.data),

  restoreApplicant: (id: number) =>
    api.put(`/admin/applicants/${id}/restore`).then((res) => res.data),

  pushToWorkspace: (id: number) =>
    api.post(`/admin/applicants/${id}/push-workspace`).then((res) => res.data),

  checkWorkspaceStatus: (id: number) =>
    api.get(`/admin/applicants/${id}/workspace-status`).then((res) => res.data),

  removeFromWorkspace: (id: number) =>
    api.delete(`/admin/applicants/${id}/workspace`).then((res) => res.data),

  getWorkspaceSyncStatus: (id: number) =>
    api.get(`/admin/applicants/${id}/workspace-sync-status`).then((res) => res.data),

  syncToWorkspace: (id: number) =>
    api.post(`/admin/applicants/${id}/workspace-sync`).then((res) => res.data),

  sendOnboardingGuide: (id: number) =>
    api.post(`/admin/applicants/${id}/send-onboarding-guide`).then((res) => res.data),

  // 회사 이메일 추천 (한글 이름 → 영문 변환)
  suggestEmail: (name: string) =>
    api.get(`/admin/suggest-email`, { params: { name } }).then((res) => res.data),

  // 명함 PDF 생성 및 이메일 발송
  requestBusinessCard: (applicantId: number, data: {
    nameKr: string; nameEn?: string;
    titleKr: string; titleEn?: string;
    email: string; mobile: string;
  }) => api.post(`/admin/applicants/${applicantId}/request-business-card`, data).then((res) => res.data),

  // 온보딩 입사 승인
  approveOnboarding: (applicantId: number, companyEmail?: string, nameEn?: string) =>
    api.post(`/admin/onboarding/${applicantId}/approve`, { companyEmail, nameEn }).then((res) => res.data),

  // 날인본 업로드
  uploadSignedDoc: (applicantId: number, docType: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);
    // 이 인스턴스의 기본 Content-Type이 application/json 이라, 지정하지 않으면
    // multipart boundary가 붙지 않아 서버(multer)가 파일을 못 읽는다.
    return api.post(`/admin/onboarding/${applicantId}/upload-signed`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },

  // 평판조회 동의서
  getReferenceConsent: (applicantId: number) =>
    api.get(`/admin/applicants/${applicantId}/reference-consent`).then((res) => res.data),

  // 교육 콘텐츠 열람 만료 기간
  getEduExpiry: () =>
    api.get('/admin/settings/edu-expiry').then((res) => res.data),

  setEduExpiry: (days: number) =>
    api.put('/admin/settings/edu-expiry', { days }).then((res) => res.data),

  // 포지션 목록 조회 (관리자용, 회사 내부 직급 제외)
  getPositionList: () =>
    api.get('/admin/positions').then((res) => res.data),

  // 포지션 추가
  createPosition: (name: string) =>
    api.post('/admin/positions', { name }).then((res) => res.data),

  // 교육 자료 관리
  getEducationManuals: () =>
    api.get('/admin/education-manuals').then((res) => res.data),

  uploadEducationManual: (docId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/education-manuals/${docId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
};

// 시험 관리 API
export const adminTestApi = {
  // 시험 목록
  getTests: () =>
    api.get('/written-test/admin/tests').then((res) => res.data),

  // 시험 상세 (문제 포함)
  getTest: (id: number) =>
    api.get(`/written-test/admin/tests/${id}`).then((res) => res.data),

  // 시험 생성
  createTest: (data: any) =>
    api.post('/written-test/admin/tests', data).then((res) => res.data),

  // 시험 수정
  updateTest: (id: number, data: any) =>
    api.put(`/written-test/admin/tests/${id}`, data).then((res) => res.data),

  // 시험 삭제
  deleteTest: (id: number) =>
    api.delete(`/written-test/admin/tests/${id}`).then((res) => res.data),

  // 문제 추가
  addQuestion: (testId: number, data: any) =>
    api.post(`/written-test/admin/tests/${testId}/questions`, data).then((res) => res.data),

  // 문제 수정
  updateQuestion: (id: number, data: any) =>
    api.put(`/written-test/admin/questions/${id}`, data).then((res) => res.data),

  // 문제 삭제
  deleteQuestion: (id: number) =>
    api.delete(`/written-test/admin/questions/${id}`).then((res) => res.data),
};

// 면접 관리 API (관리자용)
export const interviewAdminApi = {
  getInterviewers: () =>
    api.get('/interview/admin/interviewers').then((res) => res.data),

  getInterviews: () =>
    api.get('/interview/admin/interviews').then((res) => res.data),

  propose: (applicantId: number, proposedDates: { date: string; time: string }[], message?: string) =>
    api.post('/interview/admin/propose', {
      applicant_id: applicantId,
      proposed_dates: proposedDates,
      message,
    }).then((res) => res.data),

  repropose: (interviewId: number, proposedDates: { date: string; time: string }[], message?: string) =>
    api.post('/interview/admin/repropose', {
      interview_id: interviewId,
      proposed_dates: proposedDates,
      message,
    }).then((res) => res.data),

  confirmSchedule: (interviewId: number, interviewerId: number, date: string, time: string) =>
    api.put('/interview/admin/confirm', {
      interview_id: interviewId,
      interviewer_id: interviewerId,
      date,
      time,
    }).then((res) => res.data),

  evaluateInterview: (id: number, score: number, notes: string, passed: boolean) =>
    api.put(`/interview/admin/evaluate/${id}`, { score, notes, passed }).then((res) => res.data),

  cancelInterview: (id: number) =>
    api.put(`/interview/admin/cancel/${id}`).then((res) => res.data),
};

export default api;
